/**
 * Analytics tab — the reporting read of a forecast or budget version.
 *
 * It answers four questions, in the order an operator asks them:
 *   1. Where does the year land?              (KPI strip)
 *   2. Is the shape of the year sensible?     (margin trend, money by month)
 *   3. Is the plan finished?                  (completeness gauge + by department)
 *   4. Should we believe it?                  (closed-month accuracy)
 *
 * The tab switches its whole analytic frame on the version type rather than
 * just its numbers. A FORECAST is part actual and part projection, so it draws
 * the split, measures itself against the same-year budget, scores only the
 * months still open for completeness, and can be graded on the months that
 * closed. A BUDGET has no actuals in its horizon: one unbroken plan line, LY
 * as the comparison, all twelve months in scope, and nothing to grade.
 *
 * Every figure comes from useForecastData — the same payload the Workspace
 * grid renders — so this tab can never disagree with the grid.
 */

import React, { useMemo, useState } from 'react';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { useForecastData } from '../../hooks/useForecastData';
import { EmptyState } from '../shared/EmptyState';
import {
  buildMonthly,
  buildCompleteness,
  buildAccuracy,
  metricValue,
  metricSeen,
  marginPct,
  type MetricId,
  type ScopeId,
  type Basis,
  type MonthTotals,
} from './analyticsData';
import {
  ReportCard,
  ReportSegmented,
  KpiStat,
  ArcGauge,
  TrendAreaChart,
  GroupedColumnChart,
  RankedBars,
  formatCurrency,
  formatPercent,
  formatSigned,
} from './reportsNativeKit';
import { useForecastChartPalette } from '../../utils/chartPalette';

const CHART_PRIMARY = '#FF7A00';
const CHART_SECONDARY_LIGHT = '#26262D';
const CHART_TERTIARY = '#72727F';
const CHART_POSITIVE = '#12B76A';
const CHART_NEGATIVE = '#F04438';
const CHART_FORECAST_FILL = '#FFC999';

type HistoryId = 'off' | 'primary' | 'both';

const METRIC_OPTIONS = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'net', label: 'Net income' },
];

const SCOPE_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'expense', label: 'Expense' },
];

type BarPoint = Record<string, unknown> & { __rnSrc?: Record<string, unknown> };

/** Unwrap a chart datum whichever shape the kit shell handed over. */
function barDatum(point: BarPoint | undefined): Record<string, unknown> {
  return (point?.__rnSrc ?? point ?? {}) as Record<string, unknown>;
}

function money(value: number | null | undefined, currency?: string | null): string {
  return formatCurrency(value, { currency: currency || 'USD', compact: true });
}

export function AnalyticsPage({ currency }: { currency?: string | null }) {
  const { state } = useForecastV3();
  const { orgId, locationId, year, currentVersion } = state;
  const { monthVersions } = useMonthlyVersions();

  const [metric, setMetric] = useState<MetricId>('revenue');
  const [accMetric, setAccMetric] = useState<MetricId>('revenue');
  const [history, setHistory] = useState<HistoryId>('primary');
  const [scope, setScope] = useState<ScopeId>('all');

  const isBudgetVersion = currentVersion?.type === 'Budget';
  const lockedMonths = useMemo(
    () => currentVersion?.lockedMonths ?? monthVersions.filter((m) => m.status === 'locked').map((m) => m.month),
    [currentVersion?.lockedMonths, monthVersions]
  );

  const {
    accounts,
    closedMonthKeys,
    financialCloseDay,
    isLoading,
    error,
  } = useForecastData({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    year,
    versionId: currentVersion?.id ?? null,
    requireVersion: true,
    lockedMonths,
    enabled: !!orgId && !!locationId,
    roomStatSource: currentVersion?.roomStatSource ?? 'pms',
    actualRollInEnabled: currentVersion?.actualRollInEnabled ?? true,
  });

  // Classic keeps the historical constants; the New look follows the palette.
  const chartPalette = useForecastChartPalette();
  const ink = chartPalette.inset
    ? {
        primary: chartPalette.colors.primary,
        secondary: chartPalette.colors.secondary,
        tertiary: chartPalette.colors.tertiary,
        positive: chartPalette.colors.positive,
        negative: chartPalette.colors.negative,
        forecastFill: chartPalette.forecastFill,
      }
    : {
        primary: CHART_PRIMARY,
        secondary: CHART_SECONDARY_LIGHT,
        tertiary: CHART_TERTIARY,
        positive: CHART_POSITIVE,
        negative: CHART_NEGATIVE,
        forecastFill: CHART_FORECAST_FILL,
      };

  const months = useMemo(
    () => buildMonthly({ accounts, closedMonthKeys, lockedMonths }),
    [accounts, closedMonthKeys, lockedMonths]
  );

  const completeness = useMemo(
    () => buildCompleteness({ accounts, closedMonthKeys, isBudgetVersion, scope }),
    [accounts, closedMonthKeys, isBudgetVersion, scope]
  );

  const accuracy = useMemo(() => buildAccuracy(months, accMetric), [months, accMetric]);

  /* A forecast is split at the last closed month; a budget never is. The index
     is what turns one line into solid-then-dashed rather than two series. */
  const lastClosedIndex = useMemo(() => {
    if (isBudgetVersion) return -1;
    let last = -1;
    months.forEach((m, i) => { if (m.closed) last = i; });
    return last;
  }, [months, isBudgetVersion]);

  /* Which series the comparison segment turns on. A forecast is judged against
     the plan it was meant to hit; a budget against the year it replaces. */
  const comparisons = useMemo(() => {
    if (history === 'off') return [] as { basis: Basis; label: string; short: string; color: string; dash: string }[];
    const primary = isBudgetVersion
      ? { basis: 'lyActual' as Basis, label: 'Last year actual', short: 'LY', color: ink.tertiary, dash: '5 4' }
      : { basis: 'budget' as Basis, label: 'Budget', short: 'Budget', color: ink.tertiary, dash: '5 4' };
    const secondary = isBudgetVersion
      ? { basis: 'budget' as Basis, label: 'Prior-year budget', short: 'PY budget', color: ink.secondary, dash: '1 4' }
      : { basis: 'lyActual' as Basis, label: 'Last year actual', short: 'LY', color: ink.secondary, dash: '1 4' };
    return history === 'both' ? [primary, secondary] : [primary];
  }, [history, isBudgetVersion, ink.tertiary, ink.secondary]);

  const compareBasis: Basis = isBudgetVersion ? 'lyActual' : 'budget';
  const compareLabel = isBudgetVersion ? 'LY' : 'Budget';

  /* One sparkline per KPI tile, so the top row carries shape as well as a
     number — the pattern the P&L report already uses. */
  const spark = useMemo(() => {
    const pts = (fn: (m: MonthTotals) => number | null) =>
      months.map((m) => ({ label: m.label, actual: fn(m) }));
    return {
      revenue: pts((m) => metricValue(m, 'revenue', 'current')),
      expenses: pts((m) => metricValue(m, 'expenses', 'current')),
      net: pts((m) => metricValue(m, 'net', 'current')),
      margin: pts((m) => marginPct(m, 'current')),
    };
  }, [months]);

  // ------------------------------------------------------------ KPI strip
  const kpis = useMemo(() => {
    const sum = (m: MetricId, basis: Basis) =>
      months.reduce((acc, month) => acc + metricValue(month, m, basis), 0);

    const revenue = sum('revenue', 'current');
    const expenses = sum('expenses', 'current');
    const net = revenue - expenses;
    const cmpRevenue = sum('revenue', compareBasis);
    const cmpExpenses = sum('expenses', compareBasis);
    const cmpNet = cmpRevenue - cmpExpenses;

    const pctDelta = (a: number, b: number) => (Math.abs(b) > 0.005 ? ((a - b) / Math.abs(b)) * 100 : null);
    const margin = Math.abs(revenue) > 0.005 ? (net / revenue) * 100 : null;
    const cmpMargin = Math.abs(cmpRevenue) > 0.005 ? (cmpNet / cmpRevenue) * 100 : null;

    /* Growth is not automatically good: expenses rising above plan is the
       unfavourable direction, so that tile inverts. */
    const delta = (value: number | null, label: string, favourableWhenPositive = true) => {
      if (value === null) return null;
      const favourable = favourableWhenPositive ? value >= 0 : value <= 0;
      return (
        <span className={`rn-delta ${favourable ? 'rn-delta-success' : 'rn-delta-error'}`}>
          {label}
        </span>
      );
    };

    const closedCount = months.filter((m) => m.closed).length;
    const coverage = isBudgetVersion
      ? '12 plan months'
      : `${closedCount} mo actual, ${12 - closedCount} forecast`;
    const trendSub = isBudgetVersion ? `Monthly · FY${year}` : `Monthly · ${coverage}`;

    const revPct = pctDelta(revenue, cmpRevenue);
    const expPct = pctDelta(expenses, cmpExpenses);
    const netPct = pctDelta(net, cmpNet);

    return [
      {
        key: 'revenue',
        label: 'Total revenue',
        value: money(revenue, currency),
        chip: delta(revPct, revPct === null ? '' : `${formatSigned(revPct, 'percent1')} vs ${compareLabel.toLowerCase()}`),
        sub: `${compareLabel} ${money(cmpRevenue, currency)}`,
        spark: { data: spark.revenue, sub: trendSub, format: 'currencyCompact' },
      },
      {
        key: 'expenses',
        label: 'Total expenses',
        value: money(expenses, currency),
        chip: delta(expPct, expPct === null ? '' : `${formatSigned(expPct, 'percent1')} vs ${compareLabel.toLowerCase()}`, false),
        sub: `${compareLabel} ${money(cmpExpenses, currency)}`,
        spark: { data: spark.expenses, sub: trendSub, format: 'currencyCompact' },
      },
      {
        key: 'net',
        label: 'Net income',
        value: money(net, currency),
        chip: delta(netPct, netPct === null ? '' : `${formatSigned(netPct, 'percent1')} vs ${compareLabel.toLowerCase()}`),
        sub: `${compareLabel} ${money(cmpNet, currency)} · ${formatSigned(net - cmpNet, 'currencyCompact', { currency: currency || 'USD' })}`,
        spark: { data: spark.net, sub: trendSub, format: 'currencyCompact' },
      },
      {
        key: 'margin',
        label: 'Net margin',
        value: margin === null ? '—' : formatPercent(margin),
        chip: margin !== null && cmpMargin !== null
          ? delta(margin - cmpMargin, `${formatSigned(margin - cmpMargin, 'number1')} pts`)
          : null,
        sub: cmpMargin === null ? `no ${compareLabel.toLowerCase()} to compare` : `${compareLabel} ${formatPercent(cmpMargin)}`,
        spark: { data: spark.margin, sub: trendSub, format: 'percent1' },
      },
    ];
  }, [months, compareBasis, compareLabel, currency, isBudgetVersion, spark, year]);

  // -------------------------------------------------------- margin trend
  const marginRows = useMemo(
    () => months.map((month, i) => {
      const row: Record<string, unknown> = {
        month: month.label,
        plan: marginPct(month, 'current'),
        isForecast: !isBudgetVersion && i > lastClosedIndex,
      };
      comparisons.forEach((c) => {
        row[c.basis] = metricSeen(month, c.basis) ? marginPct(month, c.basis) : null;
      });
      return row;
    }),
    [months, comparisons]
  );

  /* Columns, not a line. A smoothed line through a ratio that swings from
     −50% to +100% spends most of its height on empty space and reads as one
     giant swoop; columns off a zero baseline put every month on the same
     footing and let a negative month announce itself by direction. */
  const marginSeries = useMemo(() => [
    {
      key: 'plan',
      label: isBudgetVersion ? 'Budget' : 'This version',
      fill: (point: BarPoint) => (barDatum(point).isForecast ? ink.forecastFill : ink.primary),
    },
    ...comparisons.map((c) => ({ key: c.basis, label: c.label, color: c.color })),
  ], [comparisons, isBudgetVersion, ink.forecastFill, ink.primary]);

  // ------------------------------------------------------- money by month
  const moneyRows = useMemo(
    () => months.map((month, i) => {
      const row: Record<string, unknown> = {
        month: month.label,
        plan: metricValue(month, metric, 'current'),
        isForecast: !isBudgetVersion && i > lastClosedIndex,
      };
      comparisons.forEach((c) => {
        row[c.basis] = metricSeen(month, c.basis) ? metricValue(month, metric, c.basis) : null;
      });
      return row;
    }),
    [months, metric, comparisons, isBudgetVersion, lastClosedIndex]
  );

  const moneySeries = useMemo(() => [
    {
      key: 'plan',
      label: isBudgetVersion ? 'Budget' : 'This version',
      // Forecast months read as the same series, lighter — a column that has
      // not happened yet should not look as solid as one that has.
      // The band-scale BarChart hands the raw row through; the time-series
      // shells wrap it in __rnSrc. Read either so one resolver serves both.
      fill: (point: BarPoint) => (barDatum(point).isForecast ? ink.forecastFill : ink.primary),
    },
    ...comparisons.map((c) => ({ key: c.basis, label: c.label, color: c.color })),
  ], [comparisons, isBudgetVersion, ink.forecastFill, ink.primary]);

  // ------------------------------------------------------------- accuracy
  const accuracyRows = useMemo(
    () => accuracy.rows.map((row) => ({
      month: row.label,
      variance: row.variance,
      favourable: row.favourable,
    })),
    [accuracy.rows]
  );

  const accuracyStats = useMemo(() => {
    const biasCopy = accMetric === 'expenses'
      ? (accuracy.bias ?? 0) >= 0 ? 'spend ran over forecast' : 'spend ran under forecast'
      : (accuracy.bias ?? 0) >= 0 ? 'forecast ran light — actuals came in higher' : 'forecast ran heavy — actuals came in lower';
    const scored = accuracy.rows.length;
    return [
      {
        key: 'mape',
        label: 'Mean absolute error',
        value: accuracy.mape === null ? '—' : formatPercent(accuracy.mape),
        sub: `across ${scored} closed month${scored === 1 ? '' : 's'}`,
      },
      {
        key: 'bias',
        label: 'Bias',
        value: accuracy.bias === null ? '—' : formatSigned(accuracy.bias, 'percent1'),
        sub: accuracy.bias === null ? 'nothing scored yet' : biasCopy,
      },
      {
        key: 'hit',
        label: `Within ±${accuracy.tolerance}%`,
        value: `${accuracy.withinTolerance} of ${scored}`,
        sub: scored ? `${Math.round((accuracy.withinTolerance / scored) * 100)}% hit rate` : '—',
      },
      {
        key: 'worst',
        label: 'Largest miss',
        value: accuracy.largest
          ? `${accuracy.largest.label} ${formatSigned(accuracy.largest.variance, 'currencyCompact', { currency: currency || 'USD' })}`
          : '—',
        sub: accuracy.largest && accuracy.largest.variancePct !== null
          ? `${formatSigned(accuracy.largest.variancePct, 'percent1')} on a ${money(accuracy.largest.forecast, currency)} forecast`
          : '—',
      },
    ];
  }, [accuracy, accMetric, currency]);

  const marginCardSub = useMemo(() => {
    const base = 'net income ÷ total revenue';
    if (isBudgetVersion) return `${base} · plan`;
    return comparisons.length
      ? `${base} · lighter columns are forecast months, ${comparisons[0].short.toLowerCase()} alongside`
      : `${base} · lighter columns are forecast months`;
  }, [isBudgetVersion, comparisons]);

  const moneyCardSub = useMemo(() => {
    const total = months.reduce((acc, m) => acc + metricValue(m, metric, 'current'), 0);
    return `FY${year} ${money(total, currency)}`;
  }, [months, metric, year, currency]);

  // ---------------------------------------------------------------- render
  if (!currentVersion) {
    return (
      <div className="flex-1 p-6 overflow-auto">
        <EmptyState
          type="grid"
          title="No version selected"
          description="Pick a forecast or budget version in the header to see its analytics."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6 overflow-auto">
        <EmptyState type="grid" title="Could not load analytics" description={(error as Error).message} />
      </div>
    );
  }

  const closedLabel = (() => {
    const closed = months.filter((m) => m.closed);
    if (!closed.length) return 'no closed months yet';
    return `actuals through ${closed[closed.length - 1].label} ${year}`;
  })();

  const gaugeValue = completeness.pct;
  const missing = completeness.total - completeness.done;

  return (
    <div className="flex-1 overflow-auto rn-page" style={{ padding: '20px 24px 40px' }}>
      {/* Filter bar sits outside the sheet, as on every native report page. */}
      <div className="rn-filter-bar">
        <span className="rn-stat-lbl">Compare</span>
        <ReportSegmented
          size="sm"
          ariaLabel="Comparison series"
          value={history}
          onChange={(v: HistoryId) => setHistory(v)}
          options={[
            { key: 'off', label: 'Off' },
            { key: 'primary', label: isBudgetVersion ? 'LY' : 'Budget' },
            { key: 'both', label: isBudgetVersion ? 'LY + PY budget' : 'Budget + LY' },
          ]}
        />
        <div className="rn-spacer" />
        <span className="rn-asof">
          {isBudgetVersion
            ? `${year} plan · no actuals in horizon`
            : `${closedLabel} · close day ${financialCloseDay}`}
        </span>
      </div>

      {/* One joined sheet: every tile, chart and list is a cell sharing a
          hairline, so nothing floats and no inner corner is rounded. */}
      <div className="rn-sheet">
        <div className="rn-chartgrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {kpis.map((kpi) => (
            <ReportCard key={kpi.key} title={kpi.label} sub={kpi.spark.sub} className="rn-kpi-card">
              <KpiStat value={kpi.value} delta={kpi.chip} sub={kpi.sub} />
              <TrendAreaChart
                data={kpi.spark.data}
                xKey="label"
                series={[{ key: 'actual', label: kpi.label }]}
                yFormat={kpi.spark.format}
                currency={currency || 'USD'}
                height={110}
                legend={false}
                margin={{ top: 10, right: 16, bottom: 0, left: 0 }}
                loading={isLoading}
              />
            </ReportCard>
          ))}
        </div>

        <div className="rn-chartgrid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px' }}>
          <ReportCard
            title="Margin % by month"
            sub={marginCardSub}
          >
            <GroupedColumnChart
              data={marginRows}
              xKey="month"
              series={marginSeries}
              yFormat="percent1"
              yAxis="full"
              height={210}
              legend={false}
              loading={isLoading}
            />
          </ReportCard>

          <ReportCard
            title={isBudgetVersion ? 'Budget completeness' : 'Forecast completeness'}
            className="rn-kpi-card"
            right={
              <ReportSegmented
                size="sm"
                ariaLabel="Completeness scope"
                value={scope}
                onChange={(v: ScopeId) => setScope(v)}
                options={SCOPE_OPTIONS}
              />
            }
          >
            {gaugeValue === null ? (
              <div className="rn-empty">No accounts with history in scope</div>
            ) : (
              <>
                <div className="rn-kpi-gauge">
                  <ArcGauge
                    value={gaugeValue}
                    format="percent1"
                    target={95}
                    width={250}
                    sublabel={`${completeness.done} of ${completeness.total} accounts`}
                    ariaLabel={`Completeness ${gaugeValue.toFixed(1)} percent`}
                  />
                </div>
                <div className="rn-kpi-sub" style={{ padding: '0 16px 16px', lineHeight: 1.5 }}>
                  {`${completeness.scopeLabel} · dashed tick is the 95% target · `}
                  {isBudgetVersion
                    ? 'a month set deliberately to zero counts as answered'
                    : 'closed months are excluded — they are answered by their actuals'}
                </div>
              </>
            )}
          </ReportCard>
        </div>

        <div className="rn-chartgrid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px' }}>
          <ReportCard
            title={metric === 'revenue' ? 'Revenue by month' : metric === 'expenses' ? 'Expenses by month' : 'Net income by month'}
            sub={moneyCardSub}
            right={
              <ReportSegmented
                size="sm"
                ariaLabel="Metric"
                value={metric}
                onChange={(v: MetricId) => setMetric(v)}
                options={METRIC_OPTIONS}
              />
            }
          >
            <GroupedColumnChart
              data={moneyRows}
              xKey="month"
              series={moneySeries}
              yFormat="currencyCompact"
              yAxis="full"
              currency={currency || 'USD'}
              height={210}
              legend={false}
              loading={isLoading}
            />
          </ReportCard>

          <ReportCard
            title="Completeness by department"
            sub="least complete first"
          >
            <div style={{ padding: '10px 16px 16px' }}>
              <RankedBars
                sorted
                items={completeness.byDepartment.map((d) => ({
                  id: d.name,
                  label: d.name,
                  value: (d.done / d.total) * 100,
                  display: `${Math.round((d.done / d.total) * 100)}% · ${d.done}/${d.total}`,
                }))}
                max={100}
                labelWidth={124}
                amountWidth={82}
              />
            </div>
          </ReportCard>
        </div>

        {/* Closed-month accuracy — a budget has nothing to grade */}
        {!isBudgetVersion && (
          <ReportCard
            title="Forecast accuracy — closed months"
            sub={
              accuracy.rows.length === 0
                ? `0 of ${accuracy.closedCount} closed months locked`
                : `${accuracy.rows.length} of ${accuracy.closedCount} closed months · actual − forecast frozen at lock · green is favourable`
            }
            right={
              <ReportSegmented
                size="sm"
                ariaLabel="Accuracy metric"
                value={accMetric}
                onChange={(v: MetricId) => setAccMetric(v)}
                options={METRIC_OPTIONS}
              />
            }
          >
            {accuracy.rows.length === 0 ? (
              <div className="rn-empty" style={{ maxWidth: 620, margin: '0 auto', lineHeight: 1.6 }}>
                {accuracy.closedCount === 0
                  ? `Nothing to score yet — no month in ${year} has closed with posted actuals.`
                  : `${accuracy.closedCount} month${accuracy.closedCount === 1 ? ' has' : 's have'} closed, but none are locked. `
                    + 'Locking a month freezes its forecast, and that frozen value is what accuracy is measured against — '
                    + 'an unlocked month recalculates off the same actuals being scored, so it would grade itself.'}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <GroupedColumnChart
                    data={accuracyRows}
                    xKey="month"
                    series={[{
                      key: 'variance',
                      label: 'Actual − forecast',
                      fill: (point: BarPoint) => (barDatum(point).favourable ? ink.positive : ink.negative),
                    }]}
                    yFormat="currencyCompact"
                    yAxis="full"
                    currency={currency || 'USD'}
                    height={210}
                    legend={false}
                    labels
                  />
                </div>
                <div style={{ width: 1, background: 'var(--lodge-onyx-100)', flex: 'none' }} />
                <div style={{ width: 288, flex: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8px 18px 12px' }}>
                  {accuracyStats.map((stat) => (
                    <div key={stat.key} style={{ padding: '8px 0', borderBottom: '1px solid var(--rn-hairline-soft)' }}>
                      <div className="rn-stat-lbl">{stat.label}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginTop: 2 }}>
                        <span className="rn-mono" style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-.02em', color: 'var(--lodge-onyx-900)' }}>
                          {stat.value}
                        </span>
                        <span className="rn-kpi-sub" style={{ marginTop: 0 }}>{stat.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ReportCard>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
